// Selector de tercero (Proveedor) con flujo en cascada:
//
//   1) AsyncSelect contra coleccion Proveedor local.
//      - Si el usuario lo encuentra, listo.
//
//   2) Si no aparece, el mismo input acepta un NIT. Al pulsar la lupa
//      el backend lo busca en el ERP externo configurado pasandolo como
//      query param (ej: ?identification=XXX en Alegra). NO se trae todo
//      el catalogo: una sola consulta puntual por NIT.
//      - Al seleccionar un resultado externo, se crea automaticamente
//        el Proveedor local via /proveedores/crearDesdeExterno
//        (idempotente por idContable + nit).
//
//   3) Si el ERP tampoco lo tiene, "Capturar manualmente" habilita
//      nombre + nit. Se persiste en gasto.proveedorSinCrear y el
//      comprobante del cierre queda en BORRADOR sin sincronizar hasta
//      que el aprobador cree el Proveedor real y vincule el gasto.
//
// API publica:
//   value: { proveedor: Object|null, proveedorSinCrear: {nombre,nit}|null }
//   onChange: (newValue) => void

import React, { useState } from 'react';
import AsyncSelect from 'react-select/async';
import { Form, Button, Alert, Badge, Spinner, InputGroup } from 'react-bootstrap';
import {
  FaSearch, FaCheckCircle, FaExclamationTriangle, FaUserPlus, FaTimes
} from 'react-icons/fa';
import { buscarProveedoresLocal, crearProveedorDesdeExterno } from '../../api/finanzas';
import { useProveedorExternoPorNit } from '../../hooks/useProveedoresExternos';
import { Alertassw } from '../Alertas/Alertassw';

const selectStyles = {
  menu: (base) => ({ ...base, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (base) => ({ ...base, minHeight: 38 })
};

const provToOption = (p) => ({
  value: p._id,
  label: `${p.nombre}${p.nit ? ` — ${p.nit}` : ''}`,
  raw: p
});

const buscarLocal = async (texto) => {
  try {
    const resp = await buscarProveedoresLocal(texto);
    const data = resp?.data?.data || [];
    return data.map(provToOption);
  } catch (err) {
    console.error('Error buscando proveedores locales:', err);
    return [];
  }
};

const SelectorProveedorTercero = ({ value = {}, onChange }) => {
  const proveedor = value?.proveedor || null;
  const proveedorSinCrear = value?.proveedorSinCrear || null;

  const [modoManual, setModoManual] = useState(false);
  const [nitBusqueda, setNitBusqueda] = useState('');     // input del NIT
  const [nitConfirmado, setNitConfirmado] = useState(''); // disparador del hook RQ
  const [creandoLocal, setCreandoLocal] = useState(false);

  const {
    data: dataExt,
    isFetching: buscandoExt,
    isError: errorExt
  } = useProveedorExternoPorNit(nitConfirmado);

  const handleSeleccionLocal = (opt) => {
    onChange?.({ proveedor: opt ? opt.raw : null, proveedorSinCrear: null });
    setNitBusqueda('');
    setNitConfirmado('');
    setModoManual(false);
  };

  const handleBuscarERP = () => {
    const v = nitBusqueda.trim();
    if (v.length < 5) {
      Alertassw('warning', 'NIT muy corto', 'Escribe al menos 5 caracteres del NIT.');
      return;
    }
    setNitConfirmado(v); // dispara el hook (enabled cuando length >= 5)
  };

  const handleSeleccionarExterno = async (item) => {
    setCreandoLocal(true);
    try {
      // Llenar TODOS los campos del modal "Nuevo Proveedor" (ProveedoresPage.js).
      // Lo que el ERP no provee queda con default seguro. El backend valida
      // contra allowlist (anti mass-assignment); tipoTercero se fuerza a
      // PROVEEDOR_EXTERNO aunque mandemos otra cosa.
      const resp = await crearProveedorDesdeExterno({
        nombre: item.nombre,
        nit: item.nit,
        idContable: item.idContable || null,
        email: item.email || '',
        telefono: item.telefono || '',
        nombreContacto: item.nombreContacto || '',
        ciudad: item.ciudad || '',
        direccion: item.direccion || '',
        especialidades: [],
        observacion: '',
        tipoTercero: 'PROVEEDOR_EXTERNO',
        estado: 'Activo'
      });
      const proveedorLocal = resp?.data?.data;
      if (proveedorLocal) {
        onChange?.({ proveedor: proveedorLocal, proveedorSinCrear: null });
        Alertassw(
          'success',
          'Tercero asociado',
          `${proveedorLocal.nombre} fue ${resp.data.reused ? 'encontrado en Tinvaz' : 'creado en Tinvaz'} y vinculado al gasto.`
        );
        setNitBusqueda('');
        setNitConfirmado('');
      }
    } catch (err) {
      Alertassw('error', 'Error', 'No se pudo crear el proveedor local desde la data del ERP');
    } finally {
      setCreandoLocal(false);
    }
  };

  const handleActivarManual = () => {
    setModoManual(true);
    setNitConfirmado('');
    onChange?.({
      proveedor: null,
      proveedorSinCrear: {
        nombre: '',
        // si el usuario ya habia escrito un NIT para buscar, prefilearlo
        nit: nitBusqueda || ''
      }
    });
  };

  const handleCambioManual = (campo, val) => {
    onChange?.({
      proveedor: null,
      proveedorSinCrear: {
        ...(proveedorSinCrear || {}),
        [campo]: val
      }
    });
  };

  const handleLimpiar = () => {
    setModoManual(false);
    setNitBusqueda('');
    setNitConfirmado('');
    onChange?.({ proveedor: null, proveedorSinCrear: null });
  };

  // ── 1. Tercero local ya vinculado ─────────────────────────────────────
  if (proveedor && !modoManual) {
    return (
      <div>
        <Form.Label className="fw-bold">Tercero (proveedor)</Form.Label>
        <Alert variant="success" className="py-2 mb-2 d-flex justify-content-between align-items-center small">
          <span>
            <FaCheckCircle className="me-2" />
            <strong>{proveedor.nombre}</strong>
            {proveedor.nit && <code className="ms-2">{proveedor.nit}</code>}
            {proveedor.idContable && <Badge bg="info" className="ms-2">vinculado ERP</Badge>}
          </span>
          <Button variant="link" size="sm" onClick={handleLimpiar} className="p-0">
            <FaTimes /> Cambiar
          </Button>
        </Alert>
      </div>
    );
  }

  // ── 2. Captura manual ─────────────────────────────────────────────────
  if (modoManual || proveedorSinCrear) {
    return (
      <div>
        <Form.Label className="fw-bold">Tercero (captura manual)</Form.Label>
        <Alert variant="warning" className="py-2 mb-2 small d-flex justify-content-between align-items-center">
          <span>
            <FaExclamationTriangle className="me-2" />
            El tercero no existe ni en Tinvaz ni en el ERP. Captura los datos para que el aprobador lo cree despues.
          </span>
          <Button variant="link" size="sm" onClick={handleLimpiar} className="p-0 text-warning">
            <FaTimes /> Cancelar
          </Button>
        </Alert>
        <div className="row g-2">
          <div className="col-md-7">
            <Form.Control
              size="sm"
              type="text"
              placeholder="Nombre del tercero"
              value={proveedorSinCrear?.nombre || ''}
              onChange={(e) => handleCambioManual('nombre', e.target.value)}
            />
          </div>
          <div className="col-md-5">
            <Form.Control
              size="sm"
              type="text"
              placeholder="NIT / Identificacion"
              value={proveedorSinCrear?.nit || ''}
              onChange={(e) => handleCambioManual('nit', e.target.value)}
            />
          </div>
        </div>
        <small className="text-muted mt-1 d-block">
          Mientras no se cree el Proveedor real, el comprobante asociado queda en BORRADOR sin sincronizar.
        </small>
      </div>
    );
  }

  // ── 3. Default: AsyncSelect local + lupa para buscar en ERP ───────────
  const proveedoresEncontrados = dataExt?.proveedores || [];
  const ergaConfigurado = dataExt?.configurado;
  const yaBusco = nitConfirmado.length >= 5 && !buscandoExt;

  return (
    <div>
      <Form.Label className="fw-bold">Tercero (proveedor)</Form.Label>

      {/* Select local */}
      <AsyncSelect
        cacheOptions
        defaultOptions
        loadOptions={buscarLocal}
        onChange={handleSeleccionLocal}
        isClearable
        placeholder="Buscar por nombre o NIT en Tinvaz..."
        noOptionsMessage={({ inputValue }) =>
          inputValue && inputValue.length >= 2
            ? 'Sin coincidencias en Tinvaz. Usa la lupa para buscar en el ERP por NIT.'
            : 'Escribe al menos 2 caracteres'
        }
        loadingMessage={() => 'Buscando...'}
        styles={selectStyles}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
        menuPosition="fixed"
      />

      {/* Busqueda en ERP por NIT */}
      <div className="mt-2">
        <small className="text-muted d-block mb-1">
          ¿No esta en Tinvaz? Escribe el NIT y busca en el ERP externo.
        </small>
        <InputGroup size="sm">
          <Form.Control
            type="text"
            value={nitBusqueda}
            onChange={(e) => setNitBusqueda(e.target.value)}
            placeholder="NIT del tercero (ej: 901234567)"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleBuscarERP(); } }}
          />
          <Button
            variant="outline-primary"
            onClick={handleBuscarERP}
            disabled={buscandoExt || nitBusqueda.trim().length < 5}
            title="Buscar este NIT en el ERP externo"
          >
            {buscandoExt ? <Spinner animation="border" size="sm" /> : <><FaSearch className="me-1" /> Buscar</>}
          </Button>
        </InputGroup>
      </div>

      {/* Resultados de la busqueda externa */}
      {buscandoExt && (
        <div className="text-muted small mt-2 d-flex align-items-center gap-2">
          <Spinner animation="border" size="sm" /> Consultando ERP externo...
        </div>
      )}

      {errorExt && (
        <Alert variant="danger" className="py-2 small mt-2 mb-0">
          Error consultando el ERP. Verifica que el proveedor contable este activo y bien configurado.
        </Alert>
      )}

      {yaBusco && !errorExt && !ergaConfigurado && (
        <Alert variant="light" className="py-2 small mt-2 mb-0 border">
          No hay proveedor ERP configurado para terceros.
          <Button variant="link" size="sm" className="p-0 ms-2" onClick={handleActivarManual}>
            Capturar manualmente
          </Button>
        </Alert>
      )}

      {yaBusco && ergaConfigurado && proveedoresEncontrados.length === 0 && (
        <Alert variant="warning" className="py-2 small mt-2 mb-0">
          NIT <code>{nitConfirmado}</code> no encontrado en {dataExt?.providerNombre || 'el ERP'}.
          <Button variant="link" size="sm" className="p-0 ms-2" onClick={handleActivarManual}>
            <FaUserPlus className="me-1" /> Capturar manualmente
          </Button>
        </Alert>
      )}

      {proveedoresEncontrados.length > 0 && (
        <div className="border rounded mt-2" style={{ maxHeight: 220, overflowY: 'auto' }}>
          {proveedoresEncontrados.map((p, idx) => (
            <button
              key={p.idContable || p.nit || idx}
              type="button"
              className="d-flex justify-content-between align-items-center w-100 px-2 py-2 border-bottom bg-white"
              style={{ border: 'none', textAlign: 'left' }}
              onClick={() => handleSeleccionarExterno(p)}
              disabled={creandoLocal}
            >
              <div>
                <strong>{p.nombre}</strong>
                {p.nit && <code className="ms-2">{p.nit}</code>}
              </div>
              <Badge bg="info" pill>{dataExt?.providerNombre || 'ERP'}</Badge>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SelectorProveedorTercero;
